import { AlertSeverity, alertRequiresNavAttention, getAlertPageKey } from '@aloft/toolkit-shared';

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { routeToStepMap } from '../../../../hooks/validation/consts.ts';
import { useAppraisifyNavigation } from '../../../../hooks/validation/useAppraisifyNavigation.ts';
import { selectAlerts } from '../../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectCommentaryV2 } from '../../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import {
  selectIsInspectifyOrder,
  selectIsInternalValuation,
  selectIsMockOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectTriggeredAlerts } from '../../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { useAlerts } from './useAlerts.tsx';

export const useActiveAlertCount = (): {
  count: number;
  severity: AlertSeverity;
} => {
  const alerts = useAlerts();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const isMockOrder = useAppSelector(selectIsMockOrder);
  const isInternalValuation = useAppSelector(selectIsInternalValuation);
  const triggeredAlertTypes = useAppSelector(selectTriggeredAlerts);
  const triggered = useMemo(() => new Set(triggeredAlertTypes), [triggeredAlertTypes]);
  const { pathname } = useLocation();
  const { getCurrentAppraisifyRoute, isRouteWithinAlertNavFrontier } = useAppraisifyNavigation();
  const currentAppraisifyRoute = getCurrentAppraisifyRoute(pathname);

  return useMemo(() => {
    const requiringAttention = Object.values(alerts)
      .flat()
      .filter((alert) => alertRequiresNavAttention(alert, alertsCommentary, explanationContext));

    if (isInspectifyOrder && !isMockOrder && !isInternalValuation) {
      const output = requiringAttention.filter((alert) => triggered.has(alert.type));
      return {
        count: output.length,
        severity: output.some((alert) => alert.severity === 'error') ? 'error' : 'warning',
      };
    }

    const output = requiringAttention.filter((alert) => {
      const page = getAlertPageKey(alert, routeToStepMap);
      if (!currentAppraisifyRoute) {
        return false;
      }
      return isRouteWithinAlertNavFrontier(page, currentAppraisifyRoute, alerts);
    });

    return {
      count: output.length,
      severity: output.some((alert) => alert.severity === 'error') ? 'error' : 'warning',
    };
  }, [
    alerts,
    alertsCommentary,
    explanationContext,
    currentAppraisifyRoute,
    isInspectifyOrder,
    isInternalValuation,
    isMockOrder,
    isRouteWithinAlertNavFrontier,
    triggered,
  ]);
};
